import { useState, useRef, useCallback } from 'react';
import { Alert } from 'react-native';
import { useDispatch } from 'react-redux';
import type { ImageCanvasHandle } from '@components/Canvas/types';
import { CanvasLayer } from '@components/Canvas/types';
import apis from '@api/api';
import { SHOW_LOADING_MODAL, HIDE_LOADING_MODAL } from '@reduxLocal/actions/types';

export const useEasyModeCanvas = (
  windowWidth: number,
  setInputDisabled: (disabled: boolean) => void
) => {
  const dispatch = useDispatch();
  const canvasRef = useRef<ImageCanvasHandle>(null);
  const [canvasLayers, setCanvasLayers] = useState<CanvasLayer[]>([]);
  const [selectedCanvasLayerId, setSelectedCanvasLayerId] = useState<string | null>(null);
  const [showMediaSelector, setShowMediaSelector] = useState(false);

  // Canvas layer management
  const handleCanvasLayerUpdate = useCallback((layerId: string, updates: Partial<CanvasLayer>) => {
    setCanvasLayers(prev => prev.map(layer =>
      layer.id === layerId ? { ...layer, ...updates } : layer
    ));
  }, []);

  const handleCanvasLayerAdd = useCallback((layerData: Omit<CanvasLayer, 'id' | 'zIndex'>): string => {
    const maxZIndex = Math.max(0, ...canvasLayers.map(l => l.zIndex), 0);
    const newLayer: CanvasLayer = {
      ...layerData,
      id: `canvas-layer-${Date.now()}-${Math.random()}`,
      zIndex: maxZIndex + 1,
    };
    setCanvasLayers(prev => [...prev, newLayer]);
    return newLayer.id;
  }, [canvasLayers]);

  const handleCanvasLayerRemove = useCallback((layerId: string) => {
    setCanvasLayers(prev => prev.filter(layer => layer.id !== layerId));
    if (selectedCanvasLayerId === layerId) {
      setSelectedCanvasLayerId(null);
    }
  }, [selectedCanvasLayerId]);

  const handleMediaSelect = useCallback((imageUrl: string, imageData?: any) => {
    // Default new images to (roughly) the visible window width so they feel "full size" on add.
    // Note: layer coordinates/sizes should match the visible canvas in CanvasWindow.
    // CanvasWindow uses a 16:9 canvas sized to `windowWidth`.
    const CANVAS_W = Math.max(300, windowWidth);
    const CANVAS_H = Math.max(200, Math.round(CANVAS_W * 9 / 16));

    const targetCanvasWidth = Math.max(200, CANVAS_W - 48); // small padding for window chrome

    const metaW =
      (typeof imageData?.width === 'number' && imageData.width) ||
      (typeof imageData?.metadata?.width === 'number' && imageData.metadata.width) ||
      (typeof imageData?.meta?.width === 'number' && imageData.meta.width) ||
      undefined;
    const metaH =
      (typeof imageData?.height === 'number' && imageData.height) ||
      (typeof imageData?.metadata?.height === 'number' && imageData.metadata.height) ||
      (typeof imageData?.meta?.height === 'number' && imageData.meta.height) ||
      undefined;

    const aspect = metaW && metaH && metaW > 0 && metaH > 0 ? metaH / metaW : 1;
    const targetCanvasHeight = Math.min(CANVAS_H, Math.max(200, targetCanvasWidth * aspect));

    const posX = Math.max(0, (CANVAS_W - targetCanvasWidth) / 2);
    const posY = Math.max(0, (CANVAS_H - targetCanvasHeight) / 2);

    handleCanvasLayerAdd({
      type: 'image',
      url: imageUrl,
      position: { x: posX, y: posY },
      size: { width: targetCanvasWidth, height: targetCanvasHeight },
      rotation: 0,
      opacity: 1,
      blendMode: 'normal',
      locked: false,
    });
    setShowMediaSelector(false);
  }, [handleCanvasLayerAdd, windowWidth]);

  // Canvas AI enhancement
  const handleCanvasAIEnhance = useCallback(async (
    type: 'upscale' | 'stylize' | 'remove-background',
    options?: any
  ) => {
    const selectedLayer = canvasLayers.find(l => l.id === selectedCanvasLayerId);
    if (!selectedLayer || selectedLayer.type !== 'image' || !selectedLayer.url) {
      return;
    }

    try {
      setInputDisabled(true);
      // Show global loading while enhancement is running
      dispatch({ type: SHOW_LOADING_MODAL, data: { content: 'Enhancing image...' } });
      const response = await apis.enhanceProductImage(
        selectedLayer.url,
        undefined,
        type,
        2,
        options?.prompt,
        options?.style
      );

      if (response && response.success && response.enhancedImage) {
        handleCanvasLayerUpdate(selectedCanvasLayerId, {
          url: response.enhancedImage,
        });
      }
    } catch (error) {
      console.error('AI enhancement error:', error);
    } finally {
      dispatch({ type: HIDE_LOADING_MODAL });
      setInputDisabled(false);
    }
  }, [selectedCanvasLayerId, canvasLayers, handleCanvasLayerUpdate, setInputDisabled, dispatch]);

  // Canvas AI image generation
  const handleCanvasAIGenerate = useCallback(async (prompt: string) => {
    try {
      if (!prompt || prompt.trim() === '') {
        return;
      }

      setInputDisabled(true);
      // Show global loading while generation is running
      dispatch({ type: SHOW_LOADING_MODAL, data: { content: 'Generating image...' } });

      const response = await apis.generateAIImage(prompt.trim());

      if (response && response.success && response.image) {
        const imageDataUrl = response.image; // Expecting data URL from backend

        // If there's a selected image layer, replace it; otherwise add a new layer
        if (selectedCanvasLayerId) {
          const selectedLayer = canvasLayers.find(l => l.id === selectedCanvasLayerId);
          if (selectedLayer && selectedLayer.type === 'image') {
            // Replace the selected image
            handleCanvasLayerUpdate(selectedCanvasLayerId, {
              url: imageDataUrl,
            });
          } else {
            // Selected layer is not an image, add a new one
            handleMediaSelect(imageDataUrl);
          }
        } else {
          // No selected layer, add a new centered image layer
          handleMediaSelect(imageDataUrl);
        }
      }
    } catch (error) {
      console.error('AI image generation error:', error);
      // Could show a toast/alert here if you have a notification system
    } finally {
      dispatch({ type: HIDE_LOADING_MODAL });
      setInputDisabled(false);
    }
  }, [selectedCanvasLayerId, canvasLayers, handleCanvasLayerUpdate, handleMediaSelect, setInputDisabled, dispatch]);

  // Canvas export
  const handleCanvasExport = useCallback(async () => {
    if (!canvasRef.current) return;
    try {
      const imageData = await canvasRef.current.exportCanvas();
      // Pass to PostToFeed - we'll need to trigger it somehow
      console.log('Canvas exported:', imageData.substring(0, 50));
      // TODO: Integrate with PostToFeed
    } catch (error) {
      console.error('Canvas export error:', error);
    }
  }, []);

  // Canvas save to media
  const handleCanvasSaveToMedia = useCallback(async () => {
    if (!canvasRef.current) return;
    try {
      dispatch({ type: SHOW_LOADING_MODAL, data: { content: 'Exporting canvas...' } });

      // Export canvas as data URL
      const imageData = await canvasRef.current.exportCanvas();

      // Create file name with timestamp
      const fileName = `canvas-${Date.now()}.png`;

      // Prepare media payload
      // Backend expects data URL or base64 string - it will handle conversion
      const mediaPayload = {
        name: fileName,
        data: imageData, // Pass data URL directly - backend will handle conversion
        type: 'image/png',
        metadata: {
          uploadedVia: 'canvas',
          source: 'imageCanvas',
          createdAt: new Date().toISOString(),
        },
      };

      // Upload to media library (automatically uses user or company context from session)
      const result = await apis.uploadGlobalAsset(mediaPayload);

      dispatch({ type: HIDE_LOADING_MODAL });

      if (result && Array.isArray(result) && result.length > 0) {
        Alert.alert('Success', 'Canvas has been saved to your media library!');
      } else if (result && typeof result === 'string') {
        Alert.alert('Success', 'Canvas has been saved to your media library!');
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error: any) {
      console.error('Error saving canvas to media:', error);
      dispatch({ type: HIDE_LOADING_MODAL });
      Alert.alert(
        'Save Failed',
        error.message || 'Failed to save canvas to media library'
      );
    }
  }, [dispatch]);

  return {
    canvasRef,
    canvasLayers,
    setCanvasLayers,
    selectedCanvasLayerId,
    setSelectedCanvasLayerId,
    showMediaSelector,
    setShowMediaSelector,
    handleCanvasLayerUpdate,
    handleCanvasLayerAdd,
    handleCanvasLayerRemove,
    handleMediaSelect,
    handleCanvasAIEnhance,
    handleCanvasAIGenerate,
    handleCanvasExport,
    handleCanvasSaveToMedia,
  };
};




